import React, { useState, useEffect } from 'react';
import firebaseDataService from '../../../services/FirebaseDataService';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import AccountGroupModal from './AccountGroupModal';
import { ACCOUNTING_API, handleApiError } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

// Cari grup arayüzü (AddAccount.tsx'den alındı)
interface AccountGroup {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
  /** SQL'deki sayısal grup ID'si (string olarak) — id alanı Firebase ID taşıyabildiği için ayrı tutulur. */
  sqlId?: string | null;
}

interface BulkAddAccountModalProps {
  open: boolean;
  onClose: () => void;
  onAccountsAdded: () => void; // Toplu ekleme sonrası ana listeyi yenilemek için
}

const BulkAddAccountModal: React.FC<BulkAddAccountModalProps> = ({ open, onClose, onAccountsAdded }) => {
  const { t } = useTranslation('accounting');
  const localizeGroupName = (name: string) => {
    const normalized = name.trim().toLocaleLowerCase('tr-TR');
    const keyMap: Record<string, string> = {
      'bireysel cari': 'individualCurrent',
      'personel cari': 'personnelCurrent',
      'personal cari': 'personnelCurrent',
      'kurumsal cari': 'corporateCurrent',
      'genel cari grubu': 'generalCurrentGroup'
    };
    const key = keyMap[normalized];
    return key ? t(`current.groupNames.${key}`, { defaultValue: name }) : name;
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  const [accountNamesInput, setAccountNamesInput] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<AccountGroup | null>(null);
  const [preventNegativeBalance, setPreventNegativeBalance] = useState(false);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [existingAccountNames, setExistingAccountNames] = useState<string[]>([]);


  // Şirket ID'si, grupları ve mevcut carileri yükleme
  useEffect(() => {
    if (open) {
      resetForm();
      loadCompanyDataAndExistingAccounts();
    }
  }, [open]);

  const loadCompanyDataAndExistingAccounts = async () => {
    try {
      setInitialLoading(true);
      setError(null);
      setSuccessMessage(null);

      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setInitialLoading(false);
        setError(t('current.modals.bulkAdd.errors.userNotLoggedIn'));
        return;
      }

      // Cloud Functions ile kullanıcı verisini al
      const userData = await firebaseDataService.getUserData(uid);

      if (!userData) {
        setInitialLoading(false);
        setError(t('current.modals.bulkAdd.errors.userNotFound'));
        return;
      }

      const currentCompanyId = userData.companyId;
      if (!currentCompanyId) {
        setInitialLoading(false);
        setError(t('current.modals.bulkAdd.errors.companyNotFound'));
        return;
      }
      setCompanyId(currentCompanyId);
      
      // SQL'den grupları ve mevcut carileri yükle
      await Promise.all([
        loadAccountGroups(currentCompanyId),
        loadExistingAccounts(currentCompanyId)
      ]);

    } catch (err) {
      console.error('Şirket veya grup verileri yüklenirken hata:', err);
      setInitialLoading(false);
      setError(t('current.modals.bulkAdd.errors.generalSettingsLoad'));
    }
  };

  const loadAccountGroups = async (currentCompanyId: string) => {
    try {
      console.log('SQL\'den cari gruplar yükleniyor...');
      
      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_GROUP_BY_COMPANY);

      const result = response.data;
      const sqlGroups = result.data || result;
      
      // SQL'den gelen verileri frontend formatına çevir
      const groupsList: AccountGroup[] = sqlGroups.map((data: any) => ({
        id: data.firebaseId || data.id.toString(),
        sqlId: data.id != null ? data.id.toString() : null,
        name: data.name || '',
        parentId: data.parentFirebaseId || null,
        level: data.level || 1,
        createdAt: data.createdAt || '',
        branchesId: data.branchId || null
      }));
      
      groupsList.sort((a, b) => a.level - b.level || a.name.localeCompare(b.name));
      setGroups(groupsList);
      console.log(`✅ ${groupsList.length} cari grup SQL'den yüklendi`);
    } catch (error) {
      console.error('SQL cari grupları yükleme hatası:', error);
      setError(t('current.modals.bulkAdd.errors.groupLoad'));
    }
  };

  const loadExistingAccounts = async (currentCompanyId: string) => {
    try {
      console.log('[BulkAddAccountModal] SQL\'den mevcut cariler yükleniyor...');
      
      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);

      const result = response.data;
      const sqlAccounts = result.data || result;
      
      console.log(`[BulkAddAccountModal] ${sqlAccounts.length} cari SQL'den alındı`);
      
      // Cari isimlerini küçük harfe çevirip array'e al (PascalCase ve camelCase desteği)
      const names = sqlAccounts
        .filter((acc: any) => acc.CompanyName || acc.companyName)
        .map((acc: any) => (acc.CompanyName || acc.companyName).trim().toLowerCase());
      
      setExistingAccountNames(names);
      console.log(`[BulkAddAccountModal] ✅ ${names.length} mevcut cari adı yüklendi`);
    } catch (error) {
      console.error('[BulkAddAccountModal] Mevcut cariler yüklenirken hata:', error);
      setError(t('current.modals.bulkAdd.errors.existingAccountsLoad'));
    } finally {
      setInitialLoading(false);
    }
  };


  const resetForm = () => {
    setAccountNamesInput('');
    setSelectedGroup(null);
    setPreventNegativeBalance(false);
    setError(null);
    setSuccessMessage(null);
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    setInfoDialogOpen(false);
    onClose();
  };

  const handleGroupChange = (value: AccountGroup | null) => {
    setSelectedGroup(value);
  };
  
  const getGroupFullPath = (group: AccountGroup | null): string => {
    if (!group) return '';
    const parts: string[] = [localizeGroupName(group.name)];
    let currentParentId = group.parentId;
    while (currentParentId) {
      const parentGroup = groups.find(g => g.id === currentParentId);
      if (parentGroup) {
        parts.unshift(localizeGroupName(parentGroup.name));
        currentParentId = parentGroup.parentId;
      } else {
        break;
      }
    }
    return parts.join(' > ');
  };

  const getFilteredGroupOptions = () => {
    const rootGroups = groups.filter(group => group.level === 1);
    const options: {
      group: AccountGroup;
      label: string;
      level: number;
      isParent: boolean;
      parentName?: string;
    }[] = [];
    
    rootGroups.forEach(rootGroup => {
      options.push({
        group: rootGroup,
        label: localizeGroupName(rootGroup.name),
        level: rootGroup.level,
        isParent: true
      });
      const subGroups = groups.filter(group => group.parentId === rootGroup.id);
      subGroups.forEach(subGroup => {
        options.push({
          group: subGroup,
          label: localizeGroupName(subGroup.name),
          level: subGroup.level,
          isParent: false,
          parentName: localizeGroupName(rootGroup.name)
        });
        const subSubGroups = groups.filter(group => group.parentId === subGroup.id);
        subSubGroups.forEach(subSubGroup => {
          options.push({
            group: subSubGroup,
            label: localizeGroupName(subSubGroup.name),
            level: subSubGroup.level,
            isParent: false,
            parentName: `${localizeGroupName(rootGroup.name)} > ${localizeGroupName(subGroup.name)}`
          });
        });
      });
    });
    return options;
  };


  const handleSubmit = async () => {
    if (!companyId) {
      setError(t('current.modals.bulkAdd.errors.companyRetry'));
      return;
    }
    if (!selectedGroup) {
      setError(t('current.modals.bulkAdd.errors.groupRequired'));
      return;
    }

    const namesToProcess = accountNamesInput
      .split('\n')
      .map(name => name.trim())
      .filter(name => name !== '');

    if (namesToProcess.length === 0) {
      setError(t('current.modals.bulkAdd.errors.namesRequired'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    let successCount = 0;
    let skippedCount = 0;
    const errors: string[] = [];

    // Mevcut hesap adlarını SQL'den çek (submit anında en güncel hali)
    try {
      const checkResponse = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);

      const result = checkResponse.data;
        const sqlAccounts = result.data || result;
        const currentExistingNames: string[] = sqlAccounts
          .filter((acc: any) => acc.CompanyName || acc.companyName)
          .map((acc: any) => (acc.CompanyName || acc.companyName).trim().toLowerCase());
        
        console.log(`[BulkAddAccountModal] Submit anında ${currentExistingNames.length} mevcut cari kontrolü yapılıyor`);

        for (const name of namesToProcess) {
          if (currentExistingNames.includes(name.toLowerCase())) {
            console.log(`[BulkAddAccountModal] ⚠️ "${name}" zaten mevcut, atlanıyor`);
            skippedCount++;
            continue;
          }

          const newAccountData = {
            companyId: companyId,
            companyName: name,
            groupFirebaseId: selectedGroup.id,
            // Sayısal FK: grup filtresi/raporlar GroupId üzerinden sorguluyor
            groupId: selectedGroup.sqlId ? parseInt(selectedGroup.sqlId, 10) : null,
            authorizedPerson: t('current.modals.bulkAdd.defaults.authorizedPerson'),
            // Toplu kayıtta vergi no yerine TCKN yer tutucusu kullanıldığı için gerçek kişi
            personType: 'REAL',
            phone: '',
            mobilePhone: '',
            email: '',
            website: '',
            postalCode: '',
            taxOffice: '',
            taxNumber: '11111111111',
            tradeRegisterNumber: '',
            country: t('current.modals.addAccount.defaults.country'),
            city: '',
            district: '',
            address: '',
            accountCode: '',
            notes: t('current.modals.bulkAdd.defaults.note'),
            branchId: null,
            riskLimitAmount: 0,
            riskLimitCurrency: 'TRY',
            paymentDueDays: 30,
            riskLimitAction: 'işlem yaptır',
            preventNegativeBalance: preventNegativeBalance,
            createdBy: ownAuthSession.getCurrentEmail() || 'Sistem',
          };

          try {
            await accountingApi.post(ACCOUNTING_API.ACCOUNTS, newAccountData);

            successCount++;
            currentExistingNames.push(name.toLowerCase());
            console.log(`✅ Cari "${name}" SQL'e başarıyla kaydedildi`);
          } catch (err: any) {
            console.error(`Cari "${name}" kaydedilirken hata:`, err);
            const errorMessage = err.response?.data?.message || err.message || 'Cari kaydedilemedi';
            errors.push(`"${name}" kaydedilemedi: ${errorMessage}`);
          }
        }
    } catch (err: any) {
      console.error('[BulkAddAccountModal] Mevcut cariler kontrolü hatası:', err);
      setError(t('current.modals.bulkAdd.errors.existingAccountsCheck'));
      setLoading(false);
      return;
    }

    setLoading(false);
    let message = '';
    if (successCount > 0) {
      message += t('current.modals.bulkAdd.summary.success', { count: successCount });
    }
    if (skippedCount > 0) {
      message += t('current.modals.bulkAdd.summary.skipped', { count: skippedCount });
    }
    if (errors.length > 0) {
      setError(t('current.modals.bulkAdd.errors.partialFailure', { errors: errors.join(', ') }));
    }
    if (message) {
      setSuccessMessage(message.trim());
    }

    if (successCount > 0) {
      onAccountsAdded(); // Ana listeyi yenile
      setAccountNamesInput(''); // Başarılı eklemeden sonra inputu temizle
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{t('current.modals.bulkAdd.title')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton edge="end" color="inherit" onClick={() => setInfoDialogOpen(true)} aria-label={t('current.menu.information')}>
              <InfoOutlinedIcon />
            </IconButton>
            <IconButton edge="end" color="inherit" onClick={handleClose} aria-label={t('current.actions.close')}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, pt: 3 }}>
          {initialLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <Grid container spacing={3}>

            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary.main" gutterBottom>
                  {t('current.modals.bulkAdd.generalSettings')}
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={7}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Autocomplete
                          fullWidth
                          size="small"
                          options={getFilteredGroupOptions()}
                          getOptionLabel={(option) => {
                            if (option.level === 1) return localizeGroupName(option.group.name);
                            return `${option.parentName} > ${option.label}`;
                          }}
                          value={selectedGroup ? {
                            group: selectedGroup,
                            label: localizeGroupName(selectedGroup.name),
                            level: selectedGroup.level,
                            isParent: selectedGroup.level === 1,
                            parentName: getGroupFullPath(selectedGroup).split(' > ').slice(0, -1).join(' > ') || undefined
                          } : null}
                          onChange={(_, value) => handleGroupChange(value ? value.group : null)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t('current.modals.bulkAdd.group')}
                              required
                              size="small"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <GroupIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                                    {params.InputProps.startAdornment}
                                  </>
                                )
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Box sx={{ pl: (option.level - 1) * 2, display: 'flex', alignItems: 'center', width: '100%', fontWeight: option.isParent ? 'bold' : 'normal', color: option.isParent ? 'text.primary' : 'text.secondary' }}>
                                <GroupIcon fontSize="small" sx={{ mr: 1, color: option.isParent ? 'primary.main' : 'action.active', opacity: option.isParent ? 1 : 0.7 }} />
                                <Typography variant="body2" sx={{ fontWeight: option.isParent ? 'bold' : 'normal' }}>{option.label}</Typography>
                              </Box>
                            </li>
                          )}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => setIsGroupModalOpen(true)}
                          sx={{ minWidth: '40px', height: '40px', padding: 0, borderRadius: '4px' }}
                        >
                          <AddIcon />
                        </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={preventNegativeBalance}
                          onChange={(e) => setPreventNegativeBalance(e.target.checked)}
                          name="preventNegativeBalanceBulk"
                          color="primary"
                        />
                      }
                      label={t('current.modals.bulkAdd.preventNegativeBalance')}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary.main" gutterBottom>
                  {t('current.modals.bulkAdd.namesTitle')}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label={t('current.modals.bulkAdd.namesLabel')}
                  placeholder={t('current.modals.bulkAdd.namesPlaceholder')}
                  value={accountNamesInput}
                  onChange={(e) => setAccountNamesInput(e.target.value)}
                  variant="outlined"
                  helperText={t('current.modals.bulkAdd.namesHelper')}
                />
              </Paper>
            </Grid>
            
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
              </Grid>
            )}
            {successMessage && (
              <Grid item xs={12}>
                <Alert severity="success" onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>
              </Grid>
            )}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt:1, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleClose} disabled={loading}>
            {t('current.actions.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={loading || initialLoading}
          >
            {loading ? t('current.modals.bulkAdd.saving') : t('current.modals.bulkAdd.save')}
          </Button>
        </DialogActions>

        <AccountGroupModal
          open={isGroupModalOpen}
          onClose={() => {
            setIsGroupModalOpen(false);
            if (companyId) loadAccountGroups(companyId); // Grupları yeniden yükle
          }}
        />
      </Dialog>

      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('current.modals.bulkAdd.infoDialog.title', { defaultValue: 'Toplu Cari Ekleme Bilgilendirme' })}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('current.modals.bulkAdd.infoDialog.description', {
              defaultValue:
                'Bu sayfa, aynı yapıda birden fazla cari hesabı tek seferde oluşturmak için kullanılır ve toplu kayıt sürecini hızlandırır.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.bulkAdd.infoDialog.scopeTitle', { defaultValue: 'Toplu İşlemin Kapsamı' })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('current.modals.bulkAdd.infoDialog.scopeText', {
              defaultValue:
                'Seçilen cari grup ve negatif bakiye kuralı, bu eklemede oluşan tüm yeni carilere ortak kural olarak uygulanır.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.bulkAdd.infoDialog.validationTitle', { defaultValue: 'Doğrulama ve Tekrarsız Kayıt' })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('current.modals.bulkAdd.infoDialog.validationText', {
              defaultValue:
                'Sistem, girilen cari adlarını mevcut cari hesap kayıtlarıyla karşılaştırır. Aynı isimde olan kayıtlar tekrar oluşturulmaz ve otomatik olarak atlanır.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.bulkAdd.infoDialog.processingTitle', { defaultValue: 'İşleme Alınma Şekli' })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('current.modals.bulkAdd.infoDialog.processingText', {
              defaultValue:
                'Her satır bir cari olarak işlenir. Başarılı eklenen, atlanan ve hatalı satırlar işlem sonunda özetlenerek kullanıcıya geri bildirim olarak sunulur.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.bulkAdd.infoDialog.dataCompletenessTitle', { defaultValue: 'Kayıt Sonrası Tamamlama' })}
          </Typography>
          <Typography variant="body2">
            {t('current.modals.bulkAdd.infoDialog.dataCompletenessText', {
              defaultValue:
                'Toplu ekleme hızlı temel cari hesap oluşturma amaçlıdır. Detaylı iletişim, vergi, adres ve risk parametreleri sonradan cari detay ekranından güncellenebilir.'
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>
            {t('current.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkAddAccountModal; 